import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader } from "@vds/loaders";
import { Modal, ModalBody, ModalFooter } from "@vds/modals";
import { Icon } from "@vds/icons";
import { getDefaultSearchWindow } from "../../global/constants/dateTime.constants";
import Search_What from "./Search_What";
import Search_Where from "./Search_Where";
import Search_When from "./Search_When";
import { Button, TextLinkCaret } from "@vds/buttons";
import { HISTORICALSEARCH_TEST_IDS, HISTORICALSEARCH_STATICS, TYPE_OPTIONS, RANDOM_COLORS,
  SEARCH_SECTIONS, SEARCH_BUTTON_TEXT, SEARCH_PAGE_TEXT,
FORENSIC_SEARCH_DATEFORMAT_MONTHDATE, SEARCH_STATUS_PAYLOAD_DONE } from "./SearchConstants";
import { createForensicSearchResult,
  getForensicCreatedSearchStatus,
  setLoading,
  getRecentForensicSearchesRequest,
  deleteForensicSearchResult,
  showNotificationBanner } from "../../store/actions";
import SnapshotGrid from "./SnapshotGrid";
import { Body, Title } from "@vds/typography";
import { convertCamelCaseToText } from "../../global/utility/Utils";
import moment from "moment";
import { getStatusOfSearch, getColorNameFromHex, getSearchStatusVariant, getListRowResultText, hasCountableResults, buildPersonFilterLabel } from "./ForensicSearchUtil";
import { LICENSE_SKU_PATTERNS } from "../../global/constants/licensesConstants";
import VisionModalPopComponent from "../../global/components/VisionModalPopComponent";
import { notificationTypes, NOTIFICATION_MESSAGES } from "../../layouts/NotificationConfig";
import { HTTP_STATUS_CODES } from "../../global/constants/constants";
import { Tooltip } from "@mui/material";



const HistoricalSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const licensesResp = useSelector((state) => state?.licenseReducer?.licenses);
  const licensesLoaded = useSelector((state) => state?.licenseReducer?.licensesLoaded);
  const deviceListItem = useSelector((state) => state.deviceReducer);
  const [apiLoading, setApiLoading] = useState(false);
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const { forensicSearchResultList, deleteSearchResultResponse } = useSelector((state) => state.forensicSearchReducer);
  const sections = [SEARCH_SECTIONS.WHAT, SEARCH_SECTIONS.WHERE, SEARCH_SECTIONS.WHEN];

  const [type, setType] = useState(TYPE_OPTIONS[0].value);
  const [gender, setGender] = useState([]);
  const [age, setAge] = useState([]);
  const [vehicle, setVehicle] = useState([]);
  const [upperColors, setUpperColors] = useState([]);
  const [lowerColors, setLowerColors] = useState([]);
  const [vehicleColors, setVehicleColors] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchToBeDeleted, setSearchToBeDeleted] = useState({});
  const [isZeroResultFlow, setIsZeroResultFlow] = useState(false);
  const [deletingSearchId, setDeletingSearchId] = useState(null);
  const [selectedWhereOptions, setSelectedWhereOptions] = useState({
    sites: [],
    floors: [],
    areas: []
  });

  const [defaultSearchWindow] = useState(() => getDefaultSearchWindow());
  const [fromDate, setFromDate] = useState(defaultSearchWindow.fromDate);
  const [toDate, setToDate] = useState(defaultSearchWindow.toDate);
  const [timeRange, setTimeRange] = useState(defaultSearchWindow.timeRange);
  const [dateError, setDateError] = useState("");
  const [timeError, setTimeError] = useState("");
  const [fovListIdsToDisplayGroupBySites, setFovListIdsToDisplayGroupBySites] = useState({});
  const siteReducerSites = useSelector((state) => state.sitesReducer.siteTagList);

  const [forensicFirstSearch, setForensicFirstSearch] = useState([]);
  const [searchCreated, setSearchCreated] = useState(false);
  const [recentSearchesLoaded, setRecentSearchesLoaded] = useState(false);
  const [showFetchError, setShowFetchError] = useState(false);
  const [isFetchingRecentSearches, setIsFetchingRecentSearches] = useState(false);
  const [licenseBlockMessage, setLicenseBlockMessage] = useState(null);
  const hasProcessedInitialValueRef = useRef(false);

  const fetchRecentSearches = () => {
    dispatch(getRecentForensicSearchesRequest());
  };

  useEffect(() => {
    setApiLoading(false);
    setShowFetchError(false);
    hasProcessedInitialValueRef.current = false;
    // Real cached data (non-empty) already in the store -> show it instantly, no loader.
    // No cache, or a stale/unconfirmed empty cache -> show a loader until this fetch resolves.
    const hasCachedResults =
      forensicSearchResultList?.statusCode === 200 &&
      Array.isArray(forensicSearchResultList?.responseData) &&
      forensicSearchResultList.responseData.length > 0;
    setIsFetchingRecentSearches(!hasCachedResults);
    fetchRecentSearches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (location.state?.resetToTab !== undefined) {
      setCurrentFormIndex(location.state.resetToTab);
    }
  }, [location.state]);

  useEffect(() => {
    if (!licensesLoaded) return;
    const hsLicense = licensesResp?.find(l => l.sku === LICENSE_SKU_PATTERNS.FORENSIC_SEARCH_V2);
    let subTitle = null;
    if (!hsLicense || hsLicense.licenses === 0) {
      subTitle = NOTIFICATION_MESSAGES.HS_NO_LICENSE_SUBTITLE;
    } else if (hsLicense.usedlicenses === 0) {
      subTitle = NOTIFICATION_MESSAGES.HS_LICENSE_NOT_APPLIED_SUBTITLE;
    }
    setLicenseBlockMessage(subTitle);
  }, [licensesLoaded, licensesResp]);

  useEffect(() => {
    const isFreshResponse = hasProcessedInitialValueRef.current;
    hasProcessedInitialValueRef.current = true;

    if (forensicSearchResultList?.statusCode === 200) {
      const my_SearchResults = Array.isArray(forensicSearchResultList?.responseData)
        ? forensicSearchResultList.responseData
        : [];

      if (searchCreated && my_SearchResults?.length > 0) {
        setApiLoading(false);
        dispatch(showNotificationBanner({
          type: notificationTypes[66],
          priority: "info",
          title: HISTORICALSEARCH_STATICS.SEARCHRESULT_WAITING_TITLE,
          subtitle: HISTORICALSEARCH_STATICS.SEARCHRESULT_WAITING_MESSAGE,
          showNotification: true
        }));
        // responseData is prepended on create, so index 0 is always the newly created search.
        // id is echoed into the URL query string so SearchResult survives a hard refresh.
        navigate(`/searchResult?id=${my_SearchResults[0]?.id}`, { state: my_SearchResults[0] });
      }

      // A stale/persisted "empty" cache isn't proof there are still no results --
      // only trust an empty list once this mount's own fetch has actually confirmed it,
      // otherwise keep showing the fetching state instead of "No recent searches".
      if (my_SearchResults.length === 0 && !isFreshResponse) {
        return;
      }

      setForensicFirstSearch(my_SearchResults);
      setRecentSearchesLoaded(true);
      setShowFetchError(false);
      setIsFetchingRecentSearches(false);
    } else {
      setApiLoading(false);

      if (!isFreshResponse) {
        // Leftover value from before this mount's own fetch resolves -- ignore it.
        return;
      }

      if (searchCreated) {
        setSearchCreated(false);
        const serverMessage = forensicSearchResultList?.statusCode === HTTP_STATUS_CODES.CONFLICT
          ? forensicSearchResultList?.statusText
          : null;
        const notificationInfo = {
          type: notificationTypes[119],
          priority: "critical",
          showNotification: true,
          ...(serverMessage ? { data: { subTitle: serverMessage } } : {})
        };
        dispatch(showNotificationBanner(notificationInfo));
        return;
      }

      setCurrentFormIndex(0);
      setIsFetchingRecentSearches(false);
      // Real cached data exists -> fail silently, keep showing the old list.
      // No data to fall back on (never loaded, or last load was empty) -> hard error + retry.
      if (forensicFirstSearch?.length === 0 && forensicSearchResultList?.statusCode != null) {
        setShowFetchError(true);
      }
    }
  }, [forensicSearchResultList]);

  const handleRetryRecentSearches = () => {
    setShowFetchError(false);
    setIsFetchingRecentSearches(true);
    fetchRecentSearches();
  };


  const renderCurrentStep = () => {
    switch (currentFormIndex) {
      case 0:
        return (
          <Search_What
            type={type}
            setType={setType}
            gender={gender}
            setGender={setGender}
            age={age}
            setAge={setAge}
            vehicle={vehicle}
            setVehicle={setVehicle}
            upperColors={upperColors}
            setUpperColors={setUpperColors}
            lowerColors={lowerColors}
            setLowerColors={setLowerColors}
            vehicleColors={vehicleColors}
            setVehicleColors={setVehicleColors}
          />
        );
      case 1:
        return (
          <Search_Where
            selectedWhereOptions={selectedWhereOptions}
            setSelectedWhereOptions={setSelectedWhereOptions}
            setFovListIdsToDisplayGroupBySites={setFovListIdsToDisplayGroupBySites}
          />
        );
      case 2:
        return (
          <Search_When
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            setDateError={setDateError}
            setTimeError={setTimeError}
          />
        );
      default:
        return null;
    }
  };

  const handleNext = () => {
    if (currentFormIndex === sections.length - 1) {
      onSearchClick();
    } else {
      setCurrentFormIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentFormIndex > 0) {
      setCurrentFormIndex(currentFormIndex - 1);
    }
  };

  const onSearchClick = () => {
    const whenData = { fromDate, toDate, fromTime: timeRange.startTime, toTime: timeRange.endTime };
    let dateMax = new Date(toDate);
    dateMax.setTime(new Date(whenData.toDate + ", " + whenData.toTime));
    dateMax = dateMax.toISOString();
    let dateMin = new Date(fromDate);
    dateMin.setTime(new Date(whenData.fromDate + ", " + whenData.fromTime));
    dateMin = dateMin.toISOString();

    const baseFilter = { datemin: dateMin, datemax: dateMax, type };

    const payloadfilter = type === "vehicle"
      ? {
          ...baseFilter,
          ...(vehicle.length > 0 ? { vehicle } : {}),
          ...(vehicleColors.length > 0 ? { colorobject: vehicleColors.map(m => m.name.toLowerCase()) } : {}),
        }
      : {
          ...baseFilter,
          ...(gender.length > 0 ? { genderai: gender.join(",") } : {}),
          ...(age.length > 0 ? { ageai: age.join(",") } : {}),
          ...(lowerColors.length > 0 ? { colorlow: lowerColors.map(m => m.name.toLowerCase()) } : {}),
          ...(upperColors.length > 0 ? { colorhigh: upperColors.map(m => m.name.toLowerCase()) } : {}),
        };
    const createSearchPayload = {
      'target': selectedWhereOptions.targets,
      'filters': payloadfilter,
      'customdata': {
        'selectedAreas': selectedWhereOptions.areas,
        'selectedFloors': selectedWhereOptions.floors,
        'selectedSites': selectedWhereOptions.sites
      }
    }
    setApiLoading(true);
    console.log("[HistoricalSearch] search submitted, waiting for search completed event");
    dispatch(createForensicSearchResult(createSearchPayload));
    setSearchCreated(true);
  };

  const checkForSearchLicense = () => {
   // if (licensesResp.length > 0) {
    //   const searchLicense = licensesResp.some(
    //     (license) => license.displayName === "Search" && license.licenses > 0 && license.availablelicenses > 0
    //   );
    //   return !searchLicense;
    // }
    return !true;
  };

  const isNextButtonDisabled = (stepIndex = currentFormIndex) => {
    if (stepIndex === 0 && type === "person") {
      return false;
    }
    if (stepIndex === 0 && type === "vehicle") {
      return false;
    }
    if (stepIndex === 1) {
      return selectedWhereOptions?.sites.length === 0;
    }
    if (stepIndex === sections.length - 1) {
      return (
        !fromDate ||
        !toDate ||
        !timeRange.startTime ||
        !timeRange.endTime ||
        !!dateError ||
        !!timeError
      );
    }
    return false;
  }

  const handleTabClick = (index) => {
    if (index <= currentFormIndex) {
      setCurrentFormIndex(index);
      return;
    }
    for (let step = currentFormIndex; step < index; step++) {
      if (isNextButtonDisabled(step)) return;
    }
    setCurrentFormIndex(index);
  };
    const hanldeDeleteSearchClicked = (search, zeroResultFlow = false) =>{
      setSearchToBeDeleted(search);
      setIsZeroResultFlow(zeroResultFlow);
      setShowDeleteModal(true);
    }
    const handleDeleteConfirm = () => {
      setShowDeleteModal(false);
      setIsZeroResultFlow(false);
      const deleteSearchPayload = {
        id: searchToBeDeleted.id
      };
      setDeletingSearchId(searchToBeDeleted.id);
      dispatch(deleteForensicSearchResult(deleteSearchPayload));
    };

    useEffect(() => {
      if (deleteSearchResultResponse?.statusCode === 200) {
        setDeletingSearchId(null);
        dispatch(showNotificationBanner({
          type: "Success",
          priority: "success",
          title: "Search deleted successfully",
          showNotification: true
        }));
        setForensicFirstSearch((prev) => prev.filter((search) => search?.id !== searchToBeDeleted.id));
        dispatch(getRecentForensicSearchesRequest());
      } else if (deleteSearchResultResponse?.statusCode) {
        setDeletingSearchId(null);
        dispatch(showNotificationBanner({
          type: notificationTypes[61],
          priority: "critical",
          title: "Search deletion failed",
          showNotification: true
        }));
      }
    }, [deleteSearchResultResponse]);

  if (!licensesLoaded) {
    return (
      <>
        <div className="page-heading-big">{SEARCH_PAGE_TEXT.TITLE}</div>
        <Loader active fullscreen={false} surface="light" />
      </>
    );
  }

  return (
    <>

      <div className="page-heading-big">{SEARCH_PAGE_TEXT.TITLE}</div>

      {licenseBlockMessage && (
        <div className="search-index-container">
          <Body size="medium">{licenseBlockMessage}</Body>
          <TextLinkCaret surface="light" iconPosition="right" onClick={() => navigate("/licenses")}>
            Go to Licenses
          </TextLinkCaret>
        </div>
      )}

      {currentFormIndex === 0 && !licenseBlockMessage && (
        <div className="historical-searches-wrapper">
          <div className="search-index-container">
            {recentSearchesLoaded && forensicFirstSearch?.length > 0 && (
              <div className="recent-searches-heading-row">
                <p className="recent-searches-heading">{SEARCH_PAGE_TEXT.RECENT_SEARCHES}</p>
                <p className="recent-searches-info">
                  {forensicFirstSearch.length < 10
                    ? `Showing all ${forensicFirstSearch.length} search${forensicFirstSearch.length === 1 ? "" : "es"}`
                    : SEARCH_PAGE_TEXT.RECENT_SEARCHES_INFO}
                </p>
              </div>
            )}

            {isFetchingRecentSearches && (
              <div className="recent-searches-retry-row">
                <p className="recent-searches-empty hs-loading-text">{SEARCH_PAGE_TEXT.FETCHING_RECENT_SEARCHES}</p>
              </div>
            )}

            {showFetchError && (
              <div className="recent-searches-retry-row">
                <p className="recent-searches-empty">{SEARCH_PAGE_TEXT.FETCH_RECENT_SEARCHES_ERROR}</p>
                <div
                  role="button"
                  aria-label="Retry loading recent searches"
                  data-testid={HISTORICALSEARCH_TEST_IDS.RETRY_RECENT_SEARCHES_BUTTON}
                  onClick={handleRetryRecentSearches}
                >
                  <Icon name="sync" size="small" color="#6f7171" />
                </div>
              </div>
            )}

            {!isFetchingRecentSearches && !showFetchError && recentSearchesLoaded && forensicFirstSearch?.length === 0 && (
              <div className="recent-searches-retry-row">
                <p className="recent-searches-empty">{SEARCH_PAGE_TEXT.NO_RECENT_SEARCHES}</p>
                <div
                  role="button"
                  aria-label="Retry loading recent searches"
                  data-testid={HISTORICALSEARCH_TEST_IDS.RETRY_RECENT_SEARCHES_BUTTON}
                  onClick={handleRetryRecentSearches}
                >
                  <Icon name="sync" size="small" color="#6f7171" />
                </div>
              </div>
            )}
          </div>
          <div className="recent-searches-list">
            {forensicFirstSearch?.map((firstSearch) => {
              const isDeleting = firstSearch?.id === deletingSearchId;
              return (
                <div
                  key={firstSearch?.id}
                  className={`rowContainer${isDeleting ? " rowContainer--deleting" : ""}`}
                  onClick={() => {
                    if (isDeleting) return;
                    if (firstSearch?.status === SEARCH_STATUS_PAYLOAD_DONE && firstSearch?.totalhits === 0) {
                      hanldeDeleteSearchClicked(firstSearch, true);
                    } else {
                      // id is echoed into the URL query string so SearchResult survives a hard refresh.
                      navigate(`/searchResult?id=${firstSearch?.id}`, { state: firstSearch });
                    }
                  }}
                >
                  <div className={`rowContainerdiv1 rowContainerdiv1--${getSearchStatusVariant(firstSearch)}`}>
                    <div className={`rowContainerdiv1Number${hasCountableResults(firstSearch) ? "" : " rowContainerdiv1Number--regular"}`}>{getListRowResultText(firstSearch)}</div>
                    {hasCountableResults(firstSearch) && <div>{SEARCH_PAGE_TEXT.RESULTS} </div>}
                  </div>
                  <div className="rowContainerdiv2">
                    <div className="recent-searches-container">
                      {firstSearch?.filters?.type === "vehicle" && (
                        <Body size="small" weight="semibold">
                          {firstSearch?.filters?.vehicle?.length > 0 && <>Show me: {firstSearch.filters.vehicle.map(v => convertCamelCaseToText(v)).join(", ")}</>}
                          {firstSearch?.filters?.colorobject?.length > 0 && <>{" with " + firstSearch.filters.colorobject.map(c => convertCamelCaseToText(c)).join(", ")} color</>}
                          {firstSearch?.customdata?.selectedSites.length > 0 && (
                            <> on {siteReducerSites.sites?.find((site) => firstSearch?.customdata?.selectedSites.includes(site.id))?.name}</>
                          )}
                          {firstSearch?.customdata?.selectedFloors.length > 0 && (
                            <>
                              {" "}
                              at{" "}
                              {siteReducerSites.floors
                                ?.filter((floor) => floor && firstSearch?.customdata?.selectedFloors.includes(floor.id))
                                .map((m) => m.name)
                                .join(",")}
                            </>
                          )}
                          {firstSearch?.customdata?.selectedAreas.length > 0 && (
                            <>
                              {" "}
                              ,{" "}
                              {siteReducerSites.areas
                                ?.filter((area) => area && firstSearch?.customdata?.selectedAreas.includes(area.id))
                                .map((m) => m.name)
                                .join(",")}
                            </>
                          )}
                          {" from " + moment(new Date(firstSearch?.filters?.datemin)).format(FORENSIC_SEARCH_DATEFORMAT_MONTHDATE)} to{" "}
                          {moment(new Date(firstSearch?.filters?.datemax)).format(FORENSIC_SEARCH_DATEFORMAT_MONTHDATE)}
                        </Body>
                      )}

                      {firstSearch?.filters?.type === "person" && (
                        <Body size="small" weight="semibold">
                          {"Show me: " + buildPersonFilterLabel(firstSearch?.filters)}
                          {firstSearch?.customdata?.selectedSites.length > 0 && (
                            <> on {siteReducerSites.sites?.find((site) => firstSearch?.customdata?.selectedSites.includes(site.id))?.name}</>
                          )}
                          {firstSearch?.customdata?.selectedFloors.length > 0 && (
                            <>
                              {" "}
                              at{" "}
                              {siteReducerSites.floors
                                ?.filter((floor) => floor && firstSearch?.customdata?.selectedFloors.includes(floor.id))
                                .map((m) => m.name)
                                .join(",")}
                            </>
                          )}
                          {firstSearch?.customdata?.selectedAreas.length > 0 && (
                            <>
                              {" "}
                              ,{" "}
                              {siteReducerSites.areas
                                ?.filter((area) => area && firstSearch?.customdata?.selectedAreas.includes(area.id))
                                .map((m) => m.name)
                                .join(",")}
                            </>
                          )}
                          {" from " + moment(new Date(firstSearch?.filters?.datemin)).format(FORENSIC_SEARCH_DATEFORMAT_MONTHDATE)} to{" "}
                          {moment(new Date(firstSearch?.filters?.datemax)).format(FORENSIC_SEARCH_DATEFORMAT_MONTHDATE)}
                        </Body>
                      )}
                    </div>
                  </div>
                  {firstSearch?.createdon && (
                    <Tooltip title={moment(new Date(firstSearch.createdon)).format("MMMM D, YYYY [at] h:mm A")} placement="top" arrow PopperProps={{ modifiers: [{ name: "offset", options: { offset: [0, -6] } }] }}>
                      <div className="recent-search-date">{moment(new Date(firstSearch.createdon)).fromNow()}</div>
                    </Tooltip>
                  )}
                  <div className="rowContainerdiv3">
                    <div
                      role="button"
                      aria-label="Delete search"
                      data-testid={HISTORICALSEARCH_TEST_IDS.DELETE_SEARCH_BUTTON}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isDeleting) return;
                        hanldeDeleteSearchClicked(firstSearch);
                      }}
                    >
                      {isDeleting ? (
                        <span className="row-delete-spinner" data-testid={HISTORICALSEARCH_TEST_IDS.DELETE_SEARCH_SPINNER} />
                      ) : (
                        <Icon name="trash" size="small" color="#6f7171" />
                      )}{" "}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="historical-search-main-wrapper" style={licenseBlockMessage ? { pointerEvents: 'none', opacity: 0.5 } : undefined}>
        {apiLoading && <Loader active fullscreen={false} surface="light" className="search-index-loader" />}
        <div className="search-index-container">
          <div className="historical-search-info-text">
            {SEARCH_PAGE_TEXT.INFO_TEXT}
            <br />
            {SEARCH_PAGE_TEXT.INFO_TEXT_LINE2}
          </div>

          <div className="breadcrumb-and-heading"></div>
          <div>
            <div className="bulkRule-ProgressBar-container">
              {sections.map((section, index) => (
                <div key={index} className="bulkRule-ProgressSection" onClick={() => handleTabClick(index)}>
                  <div
                    className="bulkRule-TopBar"
                    style={{ background: index < currentFormIndex ? "#008331" : index === currentFormIndex ? "#000000" : "#D8DADA" }}
                  />
                  <div className={`bulkRule-SectionText ${index === currentFormIndex ? "current" : ""}`}>
                    {index + 1}. {section}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="search-index-frame">
          <div className="gray-container">{renderCurrentStep()}</div>
          <hr className="historical-search-footer-divider" />
          <div className="gray-container-footer">
            <TextLinkCaret
              surface="light"
              iconPosition="left"
              onClick={handleBack}
              disabled={currentFormIndex === 0}
              data-testid={HISTORICALSEARCH_TEST_IDS.BACK_BUTTON}
            >
              {SEARCH_BUTTON_TEXT.BACK}
            </TextLinkCaret>
            <Button size="large" use="primary" onClick={handleNext} disabled={isNextButtonDisabled() || !!licenseBlockMessage} data-testid={HISTORICALSEARCH_TEST_IDS.NEXT_BUTTON}>
              {currentFormIndex === sections.length - 1 ? SEARCH_BUTTON_TEXT.SEARCH : SEARCH_BUTTON_TEXT.NEXT}
            </Button>
          </div>
        </div>
      </div>
      {/* Render snapshot grid outside main wrapper on 'Where' step */}
      {currentFormIndex === 1 && (
        <SnapshotGrid fovListIdsToDisplayGroupBySites={fovListIdsToDisplayGroupBySites} siteReducerSites={siteReducerSites} deviceListItem={deviceListItem} />
      )}
      <div className="historical-search-delete-modal-scope">
        <VisionModalPopComponent
          opened={showDeleteModal}
          title={isZeroResultFlow ? HISTORICALSEARCH_STATICS.NO_RESULTS_FOUND_TITLE : "Delete Search!"}
          disableOutsideClick={true}
          hideCloseButton={true}
          children={
            <div>
              <Body size="medium" viewport="desktop" primitive="h2">
                {isZeroResultFlow
                  ? "Do you want to delete this search and create a new search?"
                  : "Are you sure you want to delete the search with " + searchToBeDeleted?.totalhits + " results?"}
              </Body>
            </div>
          }
          primaryButton={{
            label: isZeroResultFlow ? "Yes" : "Delete",
            onClick: () => {handleDeleteConfirm();},
            width: "350px"
          }}
          closeButton={{
            label: isZeroResultFlow ? "No" : "Cancel",
            onClick: () => {
              setShowDeleteModal(false);
              setIsZeroResultFlow(false);
            },
            width: "350px"
          }}
        >
        </VisionModalPopComponent>
      </div>
    </>
  );
};

export default HistoricalSearch;
